import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  HLM_DIALOG_SHELL,
  HlmAlertComponent,
  HlmBtnDirective,
  HlmCheckboxComponent,
  HlmIconComponent,
  HlmInputDirective,
  HlmNativeSelectDirective,
  HlmSpinnerComponent,
} from '../../../ui';
import { Domain, SubBrand, isBareHost, normalizeDomain } from './domain.model';
import { DomainService } from './domain.service';
import { SubBrandService } from '../sub-brands/sub-brand.service';
import { BrandDomainService } from './brand-domain.service';
import { BrandService } from '../brand.service';
import { PermissionService } from '../../../core/auth/permission.service';
import { mapHttpError } from '../brand-error.util';

export interface BrandDomainAddDialogData {
  brandId: number;
  brandName: string;
}

const mapError = (err: unknown): string =>
  mapHttpError(err, {
    400: 'Invalid domain — use a bare host like audelia.com (no http://, path, or port).',
    404: 'Brand not found.',
    409: 'This domain is already registered for the brand.',
  });

@Component({
  selector: 'cmt-brand-domain-add-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmNativeSelectDirective,
    HlmCheckboxComponent,
    HlmSpinnerComponent,
    HlmAlertComponent,
  ],
  templateUrl: './brand-domain-add-dialog.component.html',
})
export class BrandDomainAddDialogComponent implements OnInit {
  protected readonly data = inject<BrandDomainAddDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<BrandDomainAddDialogComponent>);
  private readonly domainService = inject(DomainService);
  private readonly subBrandService = inject(SubBrandService);
  private readonly brandDomainService = inject(BrandDomainService);
  private readonly brandService = inject(BrandService);
  private readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);

  /** Read-only users must not reach a mutation even if the dialog was opened. */
  readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  // --- Controls (PUBLIC so spec can access) ---
  readonly domainControl = new FormControl<string>('', { nonNullable: true });
  readonly mailDomainControl = new FormControl<boolean>(false, { nonNullable: true });
  readonly mxControl = new FormControl<string>('', { nonNullable: true });
  readonly defaultBrandControl = new FormControl<number | null>(this.data.brandId);
  readonly defaultSubBrandControl = new FormControl<number | null>(null);

  // --- State ---
  protected readonly globalDomains = signal<Domain[]>([]);
  protected readonly brands = signal<{ id: number; name: string }[]>([]);
  private readonly subBrands = signal<SubBrand[]>([]);

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  // Track domain input changes as a signal (field-initializer = injection context)
  private readonly domainValue = toSignal(this.domainControl.valueChanges, { initialValue: '' });

  // Track default brand selection to scope sub-brand options
  private readonly defaultBrandValue = toSignal(this.defaultBrandControl.valueChanges, {
    initialValue: this.defaultBrandControl.value,
  });

  /** Sub-brands whose parentBrandId matches the currently selected default brand. */
  readonly subBrandOptions = computed(() =>
    this.subBrands().filter(s => s.parentBrandId === this.defaultBrandValue()),
  );

  /** True when the typed domain is a valid bare-host NOT yet in the global DOMAINS table. */
  readonly needsGlobalRegister = computed<boolean>(() => {
    const v = this.domainValue();
    if (!isBareHost(v)) return false;
    const d = normalizeDomain(v);
    return !this.globalDomains().some(g => g.domain === d);
  });

  constructor() {
    // Reset defaultSubBrand when the selected brand changes and the current value
    // is no longer among the available sub-brand options.
    effect(() => {
      if (this.subBrands().length === 0) return;
      const options = this.subBrandOptions();
      const current = this.defaultSubBrandControl.value;
      if (current != null && !options.some(s => s.id === current)) {
        this.defaultSubBrandControl.setValue(null);
      }
    });
  }

  ngOnInit(): void {
    this.domainService
      .listDomains()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(domains => this.globalDomains.set(domains));

    this.subBrandService
      .listSubBrands()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(subs => this.subBrands.set(subs));

    this.brandService
      .listBrands(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(brands => {
        this.brands.set(brands.map(b => ({ id: b.id, name: b.name })));
        // Re-assert the default-brand prefill once the options exist. The native
        // <select>'s SelectControlValueAccessor does not re-sync a value that was
        // set (in the field initializer) before its <option>s were rendered, so
        // without this the picker shows blank instead of the current brand.
        if (brands.some(b => b.id === this.data.brandId)) {
          this.defaultBrandControl.setValue(this.data.brandId);
        }
      });
  }

  submit(): void {
    // Guard against double-submit (Enter key races the [disabled] flush) and
    // against a read-only user reaching the mutation via a stale/forgotten opener.
    if (this.saving() || !this.canWrite()) return;
    const raw = this.domainControl.value;
    if (!isBareHost(raw)) {
      this.domainControl.setErrors({ domainFormat: true });
      return;
    }
    const domain = normalizeDomain(raw);
    const mailDomain = this.mailDomainControl.value;

    // Guard before setting saving=true to avoid spinner flash on validation failures.
    if (this.needsGlobalRegister() && this.defaultBrandControl.value == null) {
      this.defaultBrandControl.markAsTouched();
      return;
    }

    this.saving.set(true);
    this.errorMessage.set(null);

    const createMapping = () =>
      this.brandDomainService
        .createBrandDomain(this.data.brandId, { domain, mailDomain })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.dialogRef.close(true),
          error: (err: unknown) => {
            this.saving.set(false);
            this.errorMessage.set(mapError(err));
          },
        });

    if (this.needsGlobalRegister()) {
      this.domainService
        .createDomain({
          domain,
          mx: this.mxControl.value.trim() || null,
          defaultBrand: this.defaultBrandControl.value,
          defaultSubBrand: this.defaultSubBrandControl.value ?? null,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => createMapping(),
          error: (err: unknown) => {
            this.saving.set(false);
            this.errorMessage.set('Global registration failed: ' + mapError(err));
          },
        });
    } else {
      createMapping();
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
